import React, { useEffect, useMemo, useState } from "react";

import { makePost } from "@culturehq/client";
import { Badge, Button, Form, Loader, SelectField, SubmitButton } from "@culturehq/components";
import { SelectValueWithCategory } from "@culturehq/components/dist/components/form/typings";

import { ApprovedForSocial, Story } from "chq";
import { Trans } from "app/utils";
import { StoryWithNullableQuestion } from "components/stories/post-stories-v2/types";

import { DataItem, MapField } from "./JobPostingMappingModal";

interface MapperSelectFieldProps {
  activeFieldId: number;
  field: MapField;
  onSelectedValue: (field: MapField, value: string | SelectValueWithCategory | null) => void;
}

const MapperSelectField: React.FC<MapperSelectFieldProps> = ({
  activeFieldId, field, onSelectedValue
}) => (
  <SelectField
    creatable={field.creatableByRegularUsers}
    name={field.id as any}
    options={field.options}
    placeholder={`Please select a ${field.label}`}
    multiple={field.allowMultiple}
    onChange={(value: string | SelectValueWithCategory | null) => {
      onSelectedValue(field, value);
    }}
  >
    <Trans>{field.label}</Trans>
    {activeFieldId === field.id && <Badge className="chq-bdg__active">Active</Badge>}
  </SelectField>
);

interface JobPostingMappingModalProps {
  approvedForSocial?: ApprovedForSocial;
  editImmediately?: boolean;
  externalMappers: MapField[];
  loading: boolean;
  onSelectedValue: (field: MapField, value: string | SelectValueWithCategory | null) => void;
  onSubmit: () => void;
  onUpdate: (value: ApprovedForSocial | Story | Record<string, any[]>) => void;
  open: boolean;
  selectedValues: { [key: string]: DataItem };
  story?: Story | StoryWithNullableQuestion;
}

const JobMappingForm: React.FC<JobPostingMappingModalProps> = ({
  approvedForSocial,
  editImmediately,
  externalMappers,
  loading,
  onSelectedValue,
  onSubmit,
  onUpdate,
  open,
  selectedValues,
  story
}) => {
  const [activeFieldId, setActiveFieldId] = useState<number>(-1);

  useEffect(() => {
    if (!open) setActiveFieldId(-1);
  }, [open]);

  useEffect(
    () => {
      const firstNonEmptySelected = Object.entries(selectedValues).find(([_, value]) => {
        if (Array.isArray(value)) return value.length > 0; // Check if array is not empty
        return value !== null && value !== undefined; // Check for non-null/undefined
      })?.[0];

      setActiveFieldId(firstNonEmptySelected ? parseInt(firstNonEmptySelected, 10) : -1);
    },
    [selectedValues]
  );

  const handleSubmit = async () => {
    const storyId = approvedForSocial?.entity?.id || story?.id;
    const mappersValueIds = Object.keys(selectedValues).map((key: any) => {
      const extenalMapper = externalMappers.find(mapper => `${mapper.id}` === key);
      if (extenalMapper?.creatableByRegularUsers) {
        const selectedValue: unknown = selectedValues[key]; // avoid never inference
        // eslint-disable-next-line no-nested-ternary
        const cleanedValue = typeof selectedValue === "string" // convert selectedValue to a string or number
          ? selectedValue.trim()
          : typeof selectedValue === "number"
            ? selectedValue
            : "";
        if (Array.isArray(selectedValue)) {
          return selectedValue
            .filter(mapperId => mapperId !== null && mapperId !== "")
            .map(mapperId => (
              typeof mapperId === "number"
                ? `${mapperId}`
                : { external_mapper_id: key, value: mapperId }
            ));
        }
        if (cleanedValue !== "") {
          return typeof cleanedValue === "number"
            ? [`${cleanedValue}`]
            : { external_mapper_id: key, value: cleanedValue };
        }
        return [];
      }

      // not creatable
      if (Array.isArray(selectedValues[key])) {
        return (selectedValues[key] as number[])
          .filter(mapperId => mapperId !== null)
          .map(mapperId => `${mapperId}`);
      }
      return selectedValues[key] !== null ? [`${selectedValues[key]}`] : [];
    }).flat();

    const body = { mappersValueIds };
    if (approvedForSocial || editImmediately) {
      const { entity } = await makePost<{ entity: Story }>(
        `/stories/${storyId}/external_mapper_value_associations`, body as any
      );
      if (approvedForSocial) onUpdate({ ...approvedForSocial, entity } as ApprovedForSocial);
      if (story) onUpdate(entity);
    } else {
      onUpdate(body);
    }
    onSubmit();
  };

  const initialValues = useMemo(() => (
    Object.fromEntries(
      Object.keys(selectedValues).map(key => [key, selectedValues[key]])
    ) as any), [selectedValues]
  );

  if (loading) return <Loader loading />;

  if (approvedForSocial || editImmediately) {
    return (
      <Form
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {externalMappers.map((field: MapField) => (
          <MapperSelectField
            key={field.id}
            activeFieldId={activeFieldId}
            field={field}
            onSelectedValue={onSelectedValue}
          />
        ))}
        <SubmitButton primary>
          {submitting => (
            submitting
              ? <Trans>Submitting Job Mapping...</Trans> : <Trans>Save Job Mapping</Trans>
          )}
        </SubmitButton>
      </Form>
    );
  }

  return (
    <div>
      {externalMappers.map((field: MapField) => (
        <SelectField
          key={field.id}
          creatable={field.creatableByRegularUsers}
          name={field.id as any}
          options={field.options}
          placeholder={`Please select a ${field.label}`}
          multiple={field.allowMultiple}
          onChange={(value: string | SelectValueWithCategory | null) => {
            onSelectedValue(field, value);
          }}
          value={initialValues[field.id] || (field.allowMultiple ? [] : null)}
        >
          <Trans>{field.label}</Trans>
          {activeFieldId === field.id && <Badge className="chq-bdg__active">Active</Badge>}
        </SelectField>
      ))}
      <Button primary onClick={handleSubmit}>
        <Trans>Save Job Mapping</Trans>
      </Button>
    </div>
  );
};

export default JobMappingForm;
